import isPropValid from '@emotion/is-prop-valid'
import styled from '@emotion/styled'

import { DefaultValue } from '../../enums/defaultValue'
import { Asable } from '../../types/asable'
import { getDefaultTransition } from '../../utils/getDefaultTransition'
import { ButtonColor, Size, Variant } from './types'
import { getBorderStyle, getFocusedStyle, getLoadingStyle, getSizeStyle, getVariantStyle } from './utils'
import { getActiveStyle } from './utils/getActiveStyle'
import { getDisabledStyle } from './utils/getDisabledStyle'

export interface ButtonProps extends Asable {
  active?: boolean
  centered?: boolean
  color?: ButtonColor
  disabled?: boolean
  focused?: boolean
  forceSquareRatio?: boolean
  fullWidth?: boolean
  fullRound?: boolean
  href?: string
  loading?: boolean
  size: Size
  skipLeftRounding?: boolean
  skipRightRounding?: boolean
  variant: Variant
  withIconMargin?: boolean
  borderRadius?: string
}

export const Button = styled('button', {
  shouldForwardProp: (propName) => isPropValid(propName) && propName !== 'loading' && propName !== 'color',
})<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: ${({ centered }) => (centered ? 'center' : 'unset')};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  max-width: 100%;
  border: none;
  font-family: ${DefaultValue.FontFamily};
  white-space: nowrap;
  cursor: pointer;
  ${getDefaultTransition('background-color', 'color', 'border', 'box-shadow', 'transform', 'filter')};

  &:not(:disabled) {
    &:active {
      transform: scale(${DefaultValue.ActiveElementScale});
    }
  }

  ${({ forceSquareRatio, size, fullWidth }) => getSizeStyle(size, forceSquareRatio, fullWidth)};
  ${({ variant, color }) => getVariantStyle(variant, color)};
  ${({ variant }) => getDisabledStyle(variant)};
  ${({ active, color, variant }) => getActiveStyle(variant, active, color)};
  ${({ color, loading, variant }) => loading && getLoadingStyle(variant, color)};
  ${({ skipLeftRounding, skipRightRounding, fullRound, borderRadius }) =>
    getBorderStyle(skipLeftRounding, skipRightRounding, fullRound, borderRadius)};

  &:focus-visible,
  &.is-focused {
    /*  The .is-focused class is a helper for Storybook to preview focus states
        without manual tab navigation. For runtime, :focus-visible handles accessibility.
    */
    ${({ variant }) => getFocusedStyle(variant)};
  }
`
